import React from 'react'
import PropTypes from 'prop-types'
import styles from './EmptyState.module.scss'

import markEmpty from 'styles/images/marks/empty.png'
import markNoResults from 'styles/images/marks/no-results.png'
import markNotFound from 'styles/images/marks/not-found.png'
import markError from 'styles/images/marks/error.png'
import markOffline from 'styles/images/marks/offline.png'
import markNoAccess from 'styles/images/marks/no-access.png'
import markAllClear from 'styles/images/marks/all-clear.png'
import markComingSoon from 'styles/images/marks/coming-soon.png'

const MARKS = {
  empty: markEmpty,
  'no-results': markNoResults,
  'not-found': markNotFound,
  error: markError,
  offline: markOffline,
  'no-access': markNoAccess,
  'all-clear': markAllClear,
  'coming-soon': markComingSoon,
}

export const EMPTY_STATES = {
  empty: {
    title: 'Nothing here yet',
    description: 'This list fills as work comes in. Create the first record to get started.',
  },
  'no-results': {
    title: 'No matches',
    description: 'Nothing fits the current filters. Clear one, or widen the date range.',
  },
  'not-found': {
    title: "We couldn't find that",
    description: 'The record may have been deleted, or the link may be out of date.',
  },
  error: {
    title: 'Something went wrong',
    description: "We couldn't load this. Nothing was changed — try again, and if it keeps failing, contact support.",
  },
  offline: {
    title: "We couldn't reach the source",
    description: 'The connection to the data source dropped, so what you see may be stale.',
  },
  'no-access': {
    title: "You don't have access to this",
    description: "Your role doesn't include this surface. An administrator can grant it.",
  },
  'all-clear': {
    title: 'Nothing needs your attention',
    description: "Every trigger is clear and every application is with someone else. We'll flag anything new.",
  },
  'coming-soon': {
    title: 'Not available yet',
    description: "We'll let you know when it's ready.",
  },
}

export const EMPTY_STATE_KINDS = Object.keys(EMPTY_STATES)

const cx = (...p) => p.filter(Boolean).join(' ')

const EmptyState = ({ kind, title, description, mark, action, compact, className, ...rest }) => {
  const preset = EMPTY_STATES[kind]
  if (!preset) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(
        `EmptyState: unknown kind "${kind}". Expected one of: ${EMPTY_STATE_KINDS.join(', ')}`
      )
    }
    return null
  }

  return (
    <div className={cx(styles.empty, compact && styles.compact, className)} {...rest}>
      {mark || <img className={styles.mark} src={MARKS[kind]} alt="" aria-hidden="true" />}
      {}
      <h3 className={cx(styles.title, kind === 'error' && styles.titleError)}>
        {title || preset.title}
      </h3>
      <p className={styles.description}>{description || preset.description}</p>
      {action && <div className={styles.action}>{action}</div>}
    </div>
  )
}

EmptyState.propTypes = {
  kind: PropTypes.oneOf(EMPTY_STATE_KINDS).isRequired,

  title: PropTypes.node,
  description: PropTypes.node,

  mark: PropTypes.node,

  action: PropTypes.node,
  className: PropTypes.string,
}

export default EmptyState
